import { createContext, useContext } from "react";
import type { Role } from "./session";

/**
 * Who is signed in, for the parts of the panel that have to *look* different (spec §3).
 *
 * ## A context, because this is the second consumer
 *
 * `useSession` says it plainly: one consumer and one request at the root, *and the moment a
 * second component needs this, a provider is the right answer rather than a second call*. The
 * moment arrived with the two roles. The show page and the show list both have to know whether
 * the person reading them may run a show, and neither is anywhere near the root.
 *
 * ## No provider component, and `.ts` rather than `.tsx`
 *
 * `App.tsx` renders `<RolesContext value={roles}>` directly, which React 19 allows. A one-line
 * `RolesProvider` wrapper would be the familiar shape and would cost the two hooks below their
 * fast refresh: a module that exports both a component and a function loses it for the
 * component, which is the same reason `shows/auction.ts` keeps `DEFAULT_AUCTION` out of the
 * components that use it.
 *
 * ## The panel hiding a control is not the enforcement
 *
 * It never is, anywhere in this application, and it matters more here than usual because this
 * one looks like a permission. The boundary is `requireHost` in
 * `packages/functions/src/lib/gate.ts` — a database read, on every request, refusing with 403.
 * What this does is stop a moderator being shown a button that would refuse them, which is a
 * courtesy and not a control: a moderator who edits this value in a debugger gets a panel full
 * of buttons that all answer 403.
 */
export const RolesContext = createContext<readonly Role[]>([]);

/** Every staff role this account holds, in `ROLES` order. */
export function useRoles(): readonly Role[] {
  return useContext(RolesContext);
}

/**
 * Whether this account may run a show — build it, publish it, drive its lots, broadcast, end it.
 *
 * The mirror of `mayRunShows` in `packages/core/src/auth/roles.ts`, and a copy of it for the same
 * reason the panel's `DEFAULT_AUCTION` is a copy: the panel is a separate build with no import
 * path into the domain. What keeps the two honest is that they live in one repository and change
 * together — and that the server's is the one that decides.
 */
export function useMayRunShows(): boolean {
  return useRoles().includes("host");
}
